import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { Box } from "@memorang/ui";
import { useEffect, useRef } from "react";
import { StyleSheet } from "react-native";
import { ScrollView } from "react-native";
import { Text } from "react-native-paper";

interface Message {
	type: "doctor" | "patient";
	text: string;
	timestamp: string;
}

interface ConversationTranscriptProps {
	messages: Message[];
}

const ConversationTranscript = ({ messages }: ConversationTranscriptProps) => {
	const messagesEndRef = useRef<ScrollView>(null);

	const scrollToBottom = () => {
		messagesEndRef.current?.scrollToEnd({ animated: true });
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		scrollToBottom();
	}, [messages]);

	return (
		<Box style={styles.container}>
			<ScrollView ref={messagesEndRef} style={styles.messagesContainer}>
				{messages.length === 0 ? (
					<Box style={styles.emptyState}>
						<Icon name="message-text-outline" size={48} color="#aaaaaa" />
						<Text variant="titleMedium" style={styles.emptyTitle}>
							No messages yet
						</Text>
						<Text style={styles.emptyText}>
							Start the conversation by typing a message or using voice
							recording
						</Text>
					</Box>
				) : (
					<>
						{messages.map((message, index) => (
							<Box
								key={`${message.timestamp}-${index}`}
								style={[
									styles.messageRow,
									message.type === "doctor" && styles.messageRowReverse,
								]}
							>
								<Box
									style={[
										styles.messageBox,
										message.type === "patient"
											? styles.doctorMessage
											: styles.patientMessage,
									]}
								>
									<Text style={styles.messageText}>{message.text}</Text>
									<Text style={styles.timestamp}>
										{new Date(message.timestamp).toLocaleTimeString([], {
											hour: "2-digit",
											minute: "2-digit",
										})}
									</Text>
								</Box>
							</Box>
						))}
					</>
				)}
			</ScrollView>
		</Box>
	);
};
const styles = StyleSheet.create({
	container: {
		height: "100%",
		display: "flex",
		flexDirection: "column",
		padding: 6,
	},
	title: {
		padding: 16,
		fontWeight: "500",
		// borderBottomWidth: 1,
		// borderBottomColor: "#e0e0e0",
	},
	messagesContainer: {
		flex: 1,
		overflowY: "auto",
		display: "flex",
		flexDirection: "column",
		gap: 8,
		padding: 16,
		backgroundColor: "#f8f9fa",
	},
	emptyState: {
		flex: 1,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		gap: 12,
		padding: 24,
	},
	emptyTitle: {
		color: "#666",
		marginTop: 8,
	},
	emptyText: {
		color: "#888",
		textAlign: "center",
		fontSize: 14,
	},
	messageRow: {
		width: "100%",
		display: "flex",
		flexDirection: "row",
		justifyContent: "flex-start",
		paddingRight: "20%",
	},
	messageRowReverse: {
		justifyContent: "flex-end",
		paddingRight: 0,
		paddingLeft: "20%",
	},
	messageBox: {
		padding: 12,
		borderRadius: 12,
		maxWidth: "100%",
	},
	doctorMessage: {
		backgroundColor: "#e3f2fd",
		borderBottomRightRadius: 4,
	},
	patientMessage: {
		backgroundColor: "#f5f5f5",
		borderBottomLeftRadius: 4,
	},
	messageText: {
		fontSize: 14,
		lineHeight: 20,
		marginBottom: 4,
	},
	timestamp: {
		fontSize: 10,
		color: "#666",
		alignSelf: "flex-end",
	},
	scrollAnchor: {
		height: 1,
		marginTop: 8,
	},
});

export default ConversationTranscript;
